import { api } from './client'

/**
 * 휴가 — 우리는 **계산하지 않는다.**
 *
 *   며칠인가      직원이 고른 날짜 개수. 셈이 아니라 사실이다
 *   주말·공휴일    묻지 않는다. 안 고르면 그만이다
 *   발생 일수      계산하지 않는다. 회사가 부여 일수를 입력한다
 *   잔여 부족      막지 않는다. 사실만 보여주고 승인은 관리자가 판단한다
 *
 * 시스템이 하는 산수는 `부여 - 사용` 뺄셈 하나뿐이다 (제품 범위 경계 §1).
 */

export type LeaveType = 'ANNUAL' | 'SICK' | 'HALF_DAY_AM' | 'HALF_DAY_PM' | 'SPECIAL' | 'PUBLIC_DUTY'
export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'

export const LEAVE_TYPES: { value: LeaveType; label: string }[] = [
  { value: 'ANNUAL', label: '연차' },
  { value: 'HALF_DAY_AM', label: '오전 반차' },
  { value: 'HALF_DAY_PM', label: '오후 반차' },
  { value: 'SICK', label: '병가' },
  { value: 'PUBLIC_DUTY', label: '공가' },
  { value: 'SPECIAL', label: '특별휴가' },
]

export function leaveTypeLabel(t: string): string {
  return LEAVE_TYPES.find((x) => x.value === t)?.label ?? t
}

/** 반차는 하루를 골라도 0.5 로 친다 */
export function isHalfDay(t: LeaveType): boolean {
  return t === 'HALF_DAY_AM' || t === 'HALF_DAY_PM'
}

/**
 * 사유를 꼭 받는 종류 — 서버 `dto/leave.dto.ts` 의 REASON_REQUIRED 와 같아야 한다.
 *
 * 특별휴가는 경조사·보건·포상·대체휴무를 다 받는 우산 개념이라 사유가 곧 종류다.
 * 「특별휴가」만 적혀 오면 관리자가 승인할 근거가 없다.
 */
export const REASON_REQUIRED: LeaveType[] = ['SPECIAL', 'PUBLIC_DUTY']

/**
 * 종류별 회사 규정 — `organizations.leave_config`.
 *
 * 🔴 **회사가 정한 값이다.** 「병가는 원래 차감 안 함」 같은 말은 쓰지 않는다 —
 *   무엇이 연차 차감 대상인지는 취업규칙·법 해석이라 우리가 정할 것이 아니다.
 */
export interface LeaveConfig {
  deduct: Record<string, boolean>
  label: Record<string, string>
  enabled: Record<string, boolean>
}

export function getLeaveConfig() {
  return api<LeaveConfig>('/api/leaves/config')
}

export interface LeaveRecord {
  id: string
  type: LeaveType
  status: LeaveStatus
  startDate: string
  endDate: string
  /** 직원이 고른 날짜들. 옛 기록은 비어 있다 */
  dates?: string[]
  days?: number | null
  reason?: string | null
  rejectionReason?: string | null
  employee?: { name: string; employeeNumber?: string | null }
}

export interface LeaveBalanceRow {
  employeeId: string
  name: string
  employeeNumber: string | null
  departmentName: string | null
  year: number
  totalDays: number
  usedDays: number
  /** 부여 - 사용. 여기까지가 산수다 */
  remainingDays: number
}

/** 잔여 현황 — 관리자는 전체, 부서장은 담당 부서 */
export function getLeaveBalances(year: number) {
  return api<LeaveBalanceRow[]>(`/api/leaves/balances?year=${year}`)
}

/** 부여 일수 입력 (ADMIN 전용). 회사가 정한 값을 그대로 저장한다 */
export function setLeaveBalance(input: { employeeId: string; year: number; totalDays: number }) {
  return api<LeaveBalanceRow>('/api/leaves/balances', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export interface CreateLeaveInput {
  type: LeaveType
  /** 고른 날짜들 (KST YYYY-MM-DD) */
  dates: string[]
  /** 반차는 0.5. 안 보내면 서버가 dates 개수로 친다 */
  days?: number
  reason?: string
}

export function createLeave(input: CreateLeaveInput) {
  return api<LeaveRecord>('/api/leaves', { method: 'POST', body: JSON.stringify(input) })
}

export function getLeaves(params?: { status?: LeaveStatus }) {
  const q = params?.status ? `?status=${params.status}` : ''
  return api<LeaveRecord[]>(`/api/leaves${q}`)
}

export function approveLeave(id: string) {
  return api<LeaveRecord>(`/api/leaves/${id}/approve`, { method: 'PATCH' })
}

export function rejectLeave(id: string, reason: string) {
  return api<LeaveRecord>(`/api/leaves/${id}/reject`, {
    method: 'PATCH',
    body: JSON.stringify({ reason }),
  })
}
